import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TeamMember, COMMON_TEAM_ROLES } from '@/types/team';
import { useUsers } from '@nimbitlabs/features/users';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Alert, AlertDescription, Input } from '@nimbitlabs/ui';
import { AlertCircle } from 'lucide-react';

const teamMemberSchema = z.object({
  userId: z.string().min(1, 'Please select a user'),
  role: z.string().min(1, 'Role is required').max(100, 'Role must be 100 characters or less'),
});

type TeamMemberFormData = z.infer<typeof teamMemberSchema>;

interface AddTeamMemberModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingMembers: TeamMember[];
  onAddMember: (userId: string, role: string) => Promise<void>;
}

export const AddTeamMemberModal: React.FC<AddTeamMemberModalProps> = ({
  open,
  onOpenChange,
  existingMembers,
  onAddMember
}) => {
  const { users = [], loading: usersLoading, fetchUsers } = useUsers();
  
  // Fetch users when modal opens
  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open, fetchUsers]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<TeamMemberFormData>({
    resolver: zodResolver(teamMemberSchema),
    defaultValues: {
      userId: '',
      role: 'Developer',
    },
    mode: 'onBlur',
  });

  // Filter out users who are already members
  const existingMemberEmails = existingMembers.map(m => m.email);
  const availableUsers = users.filter(user => !existingMemberEmails.includes(user.email));

  const onSubmit = async (data: TeamMemberFormData) => {
    setError(null);
    setIsSubmitting(true);
    
    try {
      await onAddMember(data.userId, data.role);
      onOpenChange(false);
      form.reset();
    } catch (err: any) {
      setError(err.message || 'Failed to add team member');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Team Member</DialogTitle>
        </DialogHeader>
        
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="userId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>User *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a user" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {usersLoading ? (
                        <SelectItem value="loading" disabled>Loading users...</SelectItem>
                      ) : availableUsers.length === 0 ? (
                        <SelectItem value="none" disabled>No available users</SelectItem>
                      ) : (
                        availableUsers.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.name} ({user.email})
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role *</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      placeholder="e.g., Backend Engineer, Lead, DevOps"
                      list="role-suggestions"
                    />
                  </FormControl>
                  <datalist id="role-suggestions">
                    {COMMON_TEAM_ROLES.map((role) => (
                      <option key={role} value={role} />
                    ))}
                  </datalist>
                  <p className="text-xs text-muted-foreground">Enter any custom role or select from suggestions</p>
                  <FormMessage />
                </FormItem>
              )}
            />


            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || availableUsers.length === 0}>
                {isSubmitting ? 'Adding...' : 'Add Member'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
